import { palette } from './palette';
import { typography } from './typography';
import { components } from './components';
import { breakpoints } from './breakpoints';
import { spacing } from './spacing';
import { zIndex } from './zIndex';
import { brandingImages } from './brandingImages';
import { MuiBreadcrumbs } from './components/MuiBreadcrumbs';
import { PaletteColor } from '@mui/material';
import { MuiDrawer } from './components/MuiDrawer';
import { MuiButton } from './components/MuiButton';
import { MuiAccordion, MuiAccordionDetails, MuiAccordionSummary } from './components/MuiAccordion';


/**
 * Interface for branding parameters
 */
export interface BrandingParams {
  dashboardImage?: string;
  logoImage?: string;
  primaryColors?: Record<string, string>;
  secondaryColors?: Record<string, string>;
}

/**
 * Creates theme overrides with optional branding parameters
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createThemeOverrides = (branding?: BrandingParams): Record<string, any> => {
  const newPalette = {
    ...palette,
    ...(branding?.primaryColors && { primary: branding.primaryColors as Partial<PaletteColor> }),
    ...(branding?.secondaryColors && { secondary: branding.secondaryColors as Partial<PaletteColor> }),
  };
  
  return {
    breakpoints,
    palette: {
      ...palette,
      ...(branding?.primaryColors && { primary: branding.primaryColors }),
      ...(branding?.secondaryColors && { secondary: branding.secondaryColors }),
    },
    typography,
    zIndex,
    components:{
      ...components,
      MuiBreadcrumbs: MuiBreadcrumbs(newPalette.custom.gray[300], newPalette.custom.gray[700], newPalette.primary[700] ?? newPalette.defaultPrimary[700], typography.fontWeightRegular, typography.fontWeightMedium, typography.fontWeightSemiBold),
      MuiDrawer: MuiDrawer(newPalette.primary[700] ?? newPalette.defaultPrimary[700], newPalette.primary[800] ?? newPalette.defaultPrimary[800], newPalette.primary.contrastText ?? newPalette.defaultPrimary.contrastText),
      MuiButton: MuiButton(newPalette.primary ?? newPalette.defaultPrimary, newPalette.custom.gray, newPalette.error, newPalette.common.white),
      MuiAccordion: MuiAccordion(newPalette.primary ?? newPalette.defaultPrimary, newPalette.custom.gray),
      MuiAccordionSummary: MuiAccordionSummary(newPalette.primary ?? newPalette.defaultPrimary, newPalette.custom.gray),
      MuiAccordionDetails: MuiAccordionDetails(newPalette.primary ?? newPalette.defaultPrimary, newPalette.custom.gray),
    },
    spacing,
    brandingImages: {
      ...brandingImages,
      ...(branding?.dashboardImage && { dashboardImage: branding.dashboardImage }),
      ...(branding?.logoImage && { logoImage: branding.logoImage }),
    },
  };
};

/**
 * Default theme overrides without branding
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const themeOverrides: Record<string, any> = createThemeOverrides();

/**
 * Creates dark theme overrides with optional branding parameters
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createDarkThemeOverrides = (branding?: BrandingParams): Record<string, any> => {
  const baseTheme = createThemeOverrides(branding);
  return {
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: 'dark',
    },
  };
};

/**
 * Default dark theme overrides without branding
 */
export const darkThemeOverrides = createDarkThemeOverrides();